import React, { useEffect, useMemo, useState } from 'react';
import { collection, getDocs, query, where } from 'firebase/firestore';
import Form from 'react-bootstrap/Form';
import Spinner from 'react-bootstrap/Spinner';
import swal from 'sweetalert';
import { db } from '../../firebase';
import '../dash.css';
import {
  formatKes,
  formatShortDate,
  getMonthKey,
  getMonthLabel,
  filterSchoolFeeIncomeRecords,
  normalizeAmount,
  toDateValue
} from '../../utils/financeWorkflow';

const resolveCurrency = (item = {}) => {
  const currency = item.displayCurrency || item.paymentCurrency || item.currency || 'KES';
  return currency === 'USD' ? 'USD' : 'KES';
};

const addCurrencyTotal = (totals, currency, amount) => {
  const nextTotals = { ...totals };
  nextTotals[currency] = (nextTotals[currency] || 0) + normalizeAmount(amount);
  return nextTotals;
};

const formatCurrencyValue = (amount, currency = 'KES') => {
  const safeCurrency = currency === 'USD' ? 'USD' : 'KES';
  const locale = safeCurrency === 'USD' ? 'en-US' : 'en-KE';
  const digits = safeCurrency === 'USD' ? 2 : 0;

  return `${safeCurrency} ${normalizeAmount(amount).toLocaleString(locale, {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits
  })}`;
};

const formatCurrencyTotals = (totals) => {
  const parts = [];

  if (normalizeAmount(totals.KES) > 0 || (!totals.USD && totals.KES !== undefined)) {
    parts.push(formatCurrencyValue(totals.KES || 0, 'KES'));
  }

  if (normalizeAmount(totals.USD) > 0) {
    parts.push(formatCurrencyValue(totals.USD || 0, 'USD'));
  }

  return parts.length ? parts.join(' / ') : formatCurrencyValue(0, 'KES');
};

const getWeekBucket = (value) => {
  const dateValue = toDateValue(value);
  if (!dateValue) return 'No due date';

  const day = dateValue.getDate();
  if (day <= 7) return 'Week 1';
  if (day <= 14) return 'Week 2';
  if (day <= 21) return 'Week 3';
  if (day <= 28) return 'Week 4';
  return 'Week 5';
};

const buildDueDateForPeriod = (value, periodKey) => {
  const baseDate = toDateValue(value);
  if (!baseDate || !periodKey) return null;

  const [year, month] = String(periodKey).split('-').map(Number);
  if (!year || !month) return null;

  const lastDayOfMonth = new Date(year, month, 0).getDate();
  return new Date(year, month - 1, Math.min(baseDate.getDate(), lastDayOfMonth));
};

const formatDueDate = (value) => {
  return value ? formatShortDate(value) : 'Date not set';
};

function ProjectedIncomeComp() {
  const [loading, setLoading] = useState(true);
  const [periodKey, setPeriodKey] = useState(getMonthKey());
  const [income, setIncome] = useState([]);
  const [students, setStudents] = useState([]);
  const [courses, setCourses] = useState([]);

  const loadPage = async () => {
    const [incomeSnapshot, studentSnapshot, courseSnapshot] = await Promise.all([
      getDocs(collection(db, 'Income')),
      getDocs(query(collection(db, 'users'), where('userStatus', '==', 'Student'))),
      getDocs(collection(db, 'courses'))
    ]);

    setIncome(incomeSnapshot.docs.map((item) => ({ id: item.id, ...item.data() })));
    setStudents(studentSnapshot.docs.map((item) => ({ id: item.id, ...item.data() })));
    setCourses(courseSnapshot.docs.map((item) => ({ id: item.id, ...item.data() })));
  };

  useEffect(() => {
    const fetchPage = async () => {
      try {
        setLoading(true);
        await loadPage();
      } catch (error) {
        console.error('Failed to load projected income page:', error);
        swal('Error', 'Projected income could not be loaded right now.', 'error');
      } finally {
        setLoading(false);
      }
    };

    fetchPage();
  }, []);

  const coursesByName = useMemo(() => {
    return courses.reduce((accumulator, course) => {
      accumulator[course.mcourseName] = {
        price: normalizeAmount(course.mcoursePrice),
        installment: normalizeAmount(course.feeInstallments)
      };
      return accumulator;
    }, {});
  }, [courses]);

  const schoolFeeIncome = useMemo(() => filterSchoolFeeIncomeRecords(income), [income]);

  const incomeByStudent = useMemo(() => {
    return schoolFeeIncome.reduce((accumulator, item) => {
      const studentId = item.studentId || item.userId;
      if (!studentId) {
        return accumulator;
      }

      if (!accumulator[studentId]) {
        accumulator[studentId] = [];
      }

      accumulator[studentId].push({
        amount: normalizeAmount(item.incomeAmount || item.amount),
        paymentDate: toDateValue(item.paymentDate || item.timeCreated)
      });
      return accumulator;
    }, {});
  }, [schoolFeeIncome]);

  const schoolFeeIncomeRows = useMemo(() => {
    return schoolFeeIncome
      .filter((item) => getMonthKey(item.paymentDate || item.timeCreated) === periodKey)
      .map((item) => ({
        ...item,
        currency: resolveCurrency(item),
        amount: normalizeAmount(item.incomeAmount || item.amount)
      }));
  }, [periodKey, schoolFeeIncome]);

  const projectedRows = useMemo(() => {
    return students
      .map((student) => {
        const studentId = student.userId || student.id;
        const course = coursesByName[student.course];
        if (!studentId || !course) return null;

        const coursePrice = normalizeAmount(course.price);
        const installment = normalizeAmount(course.installment);
        if (coursePrice <= 0 || installment <= 0) return null;

        const payments = incomeByStudent[studentId] || [];
        const totalPaid = payments.reduce((sum, payment) => sum + normalizeAmount(payment.amount), 0);
        const paidThisMonth = payments.reduce((sum, payment) => {
          return getMonthKey(payment.paymentDate) === periodKey
            ? sum + normalizeAmount(payment.amount)
            : sum;
        }, 0);

        const totalBalance = Math.max(coursePrice - totalPaid, 0);
        if (totalBalance <= 0) return null;

        const expectedThisMonth = Math.min(installment, totalBalance);
        const projectedOutstanding = Math.max(expectedThisMonth - paidThisMonth, 0);
        if (projectedOutstanding <= 0) return null;

        const dueDate = buildDueDateForPeriod(student.admissionDate || student.appDate, periodKey);

        return {
          id: student.id,
          studentId,
          studentName: student.bio || 'Student',
          course: student.course || 'Programme not set',
          campus: student.branchName || student.campus || 'Campus not set',
          dueDate,
          totalBalance,
          monthlyInstallment: expectedThisMonth,
          paidThisMonth,
          projectedOutstanding
        };
      })
      .filter(Boolean)
      .sort((first, second) => {
        if (second.projectedOutstanding !== first.projectedOutstanding) {
          return second.projectedOutstanding - first.projectedOutstanding;
        }
        return second.totalBalance - first.totalBalance;
      });
  }, [coursesByName, incomeByStudent, periodKey, students]);

  const projectedFeeTotal = useMemo(() => {
    return projectedRows.reduce((sum, student) => sum + normalizeAmount(student.projectedOutstanding), 0);
  }, [projectedRows]);

  const monthlyExpectedTotal = useMemo(() => {
    return projectedRows.reduce((sum, student) => sum + normalizeAmount(student.monthlyInstallment), 0);
  }, [projectedRows]);

  const collectedTotals = useMemo(() => {
    return schoolFeeIncomeRows.reduce((totals, item) => {
      return addCurrencyTotal(totals, item.currency, item.amount);
    }, { KES: 0, USD: 0 });
  }, [schoolFeeIncomeRows]);

  const collectedKes = normalizeAmount(collectedTotals.KES);
  const coverageRate = monthlyExpectedTotal > 0
    ? Math.min((collectedKes / monthlyExpectedTotal) * 100, 100)
    : 0;

  const activeStudentsCount = students.length;
  const averageOutstanding = projectedRows.length
    ? projectedFeeTotal / projectedRows.length
    : 0;

  const largestOutstandingStudent = useMemo(() => {
    return projectedRows.reduce((largest, currentItem) => {
      if (!largest) return currentItem;
      return currentItem.projectedOutstanding > largest.projectedOutstanding ? currentItem : largest;
    }, null);
  }, [projectedRows]);

  const soonestDueStudent = useMemo(() => {
    return [...projectedRows]
      .filter((student) => student.dueDate)
      .sort((first, second) => {
        const firstTime = toDateValue(first.dueDate)?.getTime() || 0;
        const secondTime = toDateValue(second.dueDate)?.getTime() || 0;
        return firstTime - secondTime;
      })[0] || null;
  }, [projectedRows]);

  const programmeMix = useMemo(() => {
    const grouped = projectedRows.reduce((accumulator, student) => {
      const key = student.course || 'Programme not set';
      const currentItem = accumulator[key] || {
        course: key,
        projectedOutstanding: 0,
        count: 0
      };

      currentItem.projectedOutstanding += normalizeAmount(student.projectedOutstanding);
      currentItem.count += 1;
      accumulator[key] = currentItem;
      return accumulator;
    }, {});

    return Object.values(grouped)
      .sort((first, second) => second.projectedOutstanding - first.projectedOutstanding)
      .slice(0, 6);
  }, [projectedRows]);

  const dueSchedule = useMemo(() => {
    const grouped = projectedRows.reduce((accumulator, student) => {
      const bucket = getWeekBucket(student.dueDate);
      const currentItem = accumulator[bucket] || {
        bucket,
        projectedOutstanding: 0,
        count: 0
      };

      currentItem.projectedOutstanding += normalizeAmount(student.projectedOutstanding);
      currentItem.count += 1;
      accumulator[bucket] = currentItem;
      return accumulator;
    }, {});

    return ['Week 1', 'Week 2', 'Week 3', 'Week 4', 'Week 5', 'No due date']
      .map((bucket) => grouped[bucket])
      .filter(Boolean);
  }, [projectedRows]);

  const balanceWatchlist = useMemo(() => {
    return projectedRows.slice(0, 8);
  }, [projectedRows]);

  const refreshPage = async () => {
    try {
      setLoading(true);
      await loadPage();
    } catch (error) {
      console.error('Failed to refresh projected income:', error);
      swal('Error', 'Projected income could not be refreshed right now.', 'error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="clientsComp incomePage projectedIncomePage">
      <section className="incomeHero projectedIncomeHero">
        <div className="incomeHeroCopy">
          <div className="incomeHeroTitleRow">
            <h5>Projected Income</h5>
          </div>
          <p className="projectedIncomeHeroText">
            A cleaner view of what is still expected this month based on active students, course
            installments, and the payments already recorded for the selected period.
          </p>
          <p className="incomeHeaderMeta">
            <span>{getMonthLabel(periodKey)}</span>
            <span className="incomeInlineDot" />
            <span>{projectedRows.length} students still owing</span>
            <span className="incomeInlineDot" />
            <span>{activeStudentsCount} active students</span>
          </p>
        </div>
        <div className="incomeHeroActions projectedIncomeHeroActions">
          <Form.Control
            type="month"
            value={periodKey}
            onChange={(event) => setPeriodKey(event.target.value)}
            className="projectedIncomeMonthControl"
          />
          <button type="button" className="btn btn-outline-dark" onClick={refreshPage}>
            Refresh
          </button>
        </div>
      </section>

      {loading ? (
        <div className="incomeStateCard projectedIncomeLoading">
          <Spinner animation="border" role="status" />
          <span>Loading projected income...</span>
        </div>
      ) : (
        <>
          <section className="incomeStatsGrid projectedIncomeStatsGrid">
            <article className="incomeStatCard incomeStatPrimary">
              <span className="incomeStatLabel">{getMonthLabel(periodKey)} projected fee</span>
              <h3>{formatKes(projectedFeeTotal)}</h3>
              <p>Remaining monthly fee expectation for students who have not cleared this month yet.</p>
            </article>
            <article className="incomeStatCard">
              <span className="incomeStatLabel">Collected this month</span>
              <h3>{formatCurrencyTotals(collectedTotals)}</h3>
              <p>School-fee income already recorded inside the selected month.</p>
            </article>
            <article className="incomeStatCard">
              <span className="incomeStatLabel">Students still owing</span>
              <h3>{projectedRows.length}</h3>
              <p>Students who still have an open installment amount for this period.</p>
            </article>
            <article className="incomeStatCard">
              <span className="incomeStatLabel">Coverage</span>
              <h3>{coverageRate.toFixed(0)}%</h3>
              <p>Calculated from KES collections against the expected installment amount for the month.</p>
            </article>
          </section>

          <div className="searchContainer incomeToolbar projectedIncomeToolbar">
            <div className="incomeToolbarMeta">
              <span className="incomeToolbarText">Tracking {getMonthLabel(periodKey)}</span>
              <span className="incomeInlineDot" />
              <span className="incomeToolbarText">{programmeMix.length} programmes represented</span>
            </div>
            <div className="incomeToolbarMeta">
              <span className="incomeToolbarText">Showing {projectedRows.length} outstanding students</span>
            </div>
          </div>

          <section className="projectedIncomeOverviewGrid">
            <article className="incomeTableCard projectedIncomeInsightCard">
              <div className="projectedIncomeSectionHeader">
                <div>
                  <h6>Collection outlook</h6>
                  <p>What is still open this month across the current student base.</p>
                </div>
              </div>

              <div className="projectedIncomeMetricGrid">
                <div className="projectedIncomeMetricCard">
                  <span className="projectedIncomeMetricLabel">Expected installment total</span>
                  <strong className="projectedIncomeMetricValue">{formatKes(monthlyExpectedTotal)}</strong>
                </div>
                <div className="projectedIncomeMetricCard">
                  <span className="projectedIncomeMetricLabel">Still outstanding</span>
                  <strong className="projectedIncomeMetricValue">{formatKes(projectedFeeTotal)}</strong>
                </div>
              </div>

              <div className="projectedIncomeProgressTrack" aria-hidden="true">
                <span className="projectedIncomeProgressBar" style={{ width: `${coverageRate}%` }}></span>
              </div>

              <div className="projectedIncomeInfoGrid">
                <div className="projectedIncomeInfoCard">
                  <span className="projectedIncomeMetricLabel">Largest outstanding student</span>
                  <strong className="projectedIncomeInfoValue">
                    {largestOutstandingStudent ? formatKes(largestOutstandingStudent.projectedOutstanding) : formatKes(0)}
                  </strong>
                  <p className="projectedIncomeInfoText">
                    {largestOutstandingStudent
                      ? `${largestOutstandingStudent.studentName} • ${largestOutstandingStudent.course}`
                      : 'No outstanding student balances this month'}
                  </p>
                </div>
                <div className="projectedIncomeInfoCard">
                  <span className="projectedIncomeMetricLabel">Average outstanding per student</span>
                  <strong className="projectedIncomeInfoValue">{formatKes(averageOutstanding)}</strong>
                  <p className="projectedIncomeInfoText">
                    Average remaining monthly fee across students still owing this month.
                  </p>
                </div>
                <div className="projectedIncomeInfoCard">
                  <span className="projectedIncomeMetricLabel">Next due student</span>
                  <strong className="projectedIncomeInfoValue">
                    {soonestDueStudent ? formatDueDate(soonestDueStudent.dueDate) : 'Date not set'}
                  </strong>
                  <p className="projectedIncomeInfoText">
                    {soonestDueStudent
                      ? `${soonestDueStudent.studentName} • ${soonestDueStudent.course}`
                      : 'No due date available from student admission records'}
                  </p>
                </div>
              </div>
            </article>

            <aside className="incomeTableCard projectedIncomeListCard">
              <div className="projectedIncomeSectionHeader">
                <div>
                  <h6>Due schedule</h6>
                  <p>How the open balances are spread across the month.</p>
                </div>
              </div>
              {dueSchedule.length === 0 ? (
                <div className="incomeStateCard incomeEmptyState projectedIncomeCompactState">
                  <h6>No outstanding students</h6>
                  <p>Students who still owe for {getMonthLabel(periodKey)} will appear here automatically.</p>
                </div>
              ) : (
                <div className="projectedIncomeList">
                  {dueSchedule.map((item) => (
                    <div key={item.bucket} className="projectedIncomeListRow">
                      <div className="projectedIncomeListMeta">
                        <strong>{item.bucket}</strong>
                        <span>{item.count} student{item.count === 1 ? '' : 's'}</span>
                      </div>
                      <span className="projectedIncomeListValue">{formatKes(item.projectedOutstanding)}</span>
                    </div>
                  ))}
                </div>
              )}
            </aside>
          </section>

          <section className="projectedIncomeOverviewGrid">
            <article className="incomeTableCard projectedIncomeListCard">
              <div className="projectedIncomeSectionHeader">
                <div>
                  <h6>Programme mix</h6>
                  <p>Which programmes are carrying the most outstanding balance this month.</p>
                </div>
              </div>
              {programmeMix.length === 0 ? (
                <div className="incomeStateCard incomeEmptyState projectedIncomeCompactState">
                  <h6>No programme exposure</h6>
                  <p>Once students have remaining balances for the selected month, the programme mix will show here.</p>
                </div>
              ) : (
                <div className="projectedIncomeList">
                  {programmeMix.map((item) => (
                    <div key={item.course} className="projectedIncomeListRow">
                      <div className="projectedIncomeListMeta">
                        <strong>{item.course}</strong>
                        <span>{item.count} student{item.count === 1 ? '' : 's'}</span>
                      </div>
                      <span className="projectedIncomeListValue">{formatKes(item.projectedOutstanding)}</span>
                    </div>
                  ))}
                </div>
              )}
            </article>

            <aside className="incomeTableCard projectedIncomeListCard">
              <div className="projectedIncomeSectionHeader">
                <div>
                  <h6>Students needing attention</h6>
                  <p>The largest remaining balances still open for the selected month.</p>
                </div>
              </div>
              {balanceWatchlist.length === 0 ? (
                <div className="incomeStateCard incomeEmptyState projectedIncomeCompactState">
                  <h6>No open balances</h6>
                  <p>There are no student fee balances outstanding for this month right now.</p>
                </div>
              ) : (
                <div className="projectedIncomeList">
                  {balanceWatchlist.map((student) => (
                    <div key={student.id} className="projectedIncomeListRow">
                      <div className="projectedIncomeListMeta">
                        <strong>{student.studentName}</strong>
                        <span>{student.course} • Due {formatDueDate(student.dueDate)}</span>
                      </div>
                      <span className="projectedIncomeListValue">{formatKes(student.projectedOutstanding)}</span>
                    </div>
                  ))}
                </div>
              )}
            </aside>
          </section>

          <section className="incomeTableCard projectedIncomeTableCard">
            <div className="incomeTableHeader">
              <div>
                <h6>Students with Outstanding Fee Balance</h6>
                <p className="projectedIncomeTableNote">
                  Active students who still have a remaining fee expectation for the selected month.
                </p>
              </div>
            </div>

            {projectedRows.length === 0 ? (
              <div className="incomeStateCard incomeEmptyState">
                <h6>No outstanding fee balances in {getMonthLabel(periodKey)}</h6>
                <p>Once a current student still owes part of that month&apos;s fee expectation, they will appear here automatically.</p>
              </div>
            ) : (
              <div className="table-responsive">
                <table className="table align-middle incomeRecordsTable projectedIncomeTable">
                  <thead>
                    <tr>
                      <th>Student</th>
                      <th>Programme</th>
                      <th>Due date</th>
                      <th>Monthly due</th>
                      <th>Paid this month</th>
                      <th>Outstanding now</th>
                    </tr>
                  </thead>
                  <tbody>
                    {projectedRows.map((student) => (
                      <tr key={student.id}>
                        <td>
                          <div className="projectedIncomeStudentCell">
                            <strong>{student.studentName}</strong>
                            <span className="projectedIncomeStudentMeta">
                              {student.campus} • Total balance {formatKes(student.totalBalance)}
                            </span>
                          </div>
                        </td>
                        <td>
                          <span className="incomeCoursePill">{student.course}</span>
                        </td>
                        <td>{formatDueDate(student.dueDate)}</td>
                        <td><span className="projectedIncomeAmountCell">{formatKes(student.monthlyInstallment)}</span></td>
                        <td><span className="projectedIncomeAmountCell">{formatKes(student.paidThisMonth)}</span></td>
                        <td><span className="projectedIncomeAmountCell">{formatKes(student.projectedOutstanding)}</span></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>
        </>
      )}
    </div>
  );
}

export default ProjectedIncomeComp;
